/* BuyerViewPreview — shared, miniature product-card preview used by:
 *   - FieldVisibilityWizard (standalone)
 *   - BulkEditModal (right side panel)
 *
 * Renders a buyer-style product card showing ONLY the fields the seller
 * has marked visible. Lets the seller see, in real time, what their
 * buyers will see as they flip toggles.
 *
 * Props:
 *   - product:    canonical product object (sku, name, price, ...). Pass
 *                  a sample row from the import to make the preview feel real.
 *   - visibility: { fieldName: boolean }
 *   - density:    "compact" | "comfortable" (default "comfortable")
 */
import { Package, Building2, Plus, Star, Tag } from "lucide-react";

function renderCfValue(field, raw) {
  if (raw === null || raw === undefined || raw === "") return "—";
  if (field.type === "yes_no") {
    const s = String(raw).toLowerCase();
    return ["true", "yes", "y", "1"].includes(s) ? "Yes" : ["false", "no", "n", "0"].includes(s) ? "No" : String(raw);
  }
  if (field.type === "number") return String(raw);
  return String(raw);
}

const FULFILL_LABEL = {
  wholesale: "Wholesale",
  dropship:  "Dropship",
  both:      "Wholesale + Dropship",
};

const BADGE_STYLE = {
  new_arrival:    { l: "New",     cls: "bg-emerald-50 border border-emerald-200 text-emerald-700" },
  top_seller:     { l: "Top",     cls: "bg-amber-50 border border-amber-200 text-amber-800" },
  weekly_special: { l: "Weekly",  cls: "bg-rose-50 border border-rose-200 text-rose-700" },
  todays_special: { l: "Today",   cls: "bg-blue-50 border border-blue-200 text-blue-700" },
};

export default function BuyerViewPreview({ product, visibility, customFields, label }) {
  const v = visibility || {};
  const isOn = (k) => v[k] !== false; // default to visible if not in map

  const p = product || {
    sku: "DEMO-001",
    name: "Sample Product Name",
    brand: "Acme Brand",
    category: "Body care",
    description: "Lorem ipsum dolor sit amet.",
    price: 18.50,
    retail_price: 42,
    moq: 12,
    stock: 240,
    case_size: 24,
    upc: "012345678901",
    image: null,
    fulfillment: "wholesale",
    badge: "new_arrival",
    map_price: 36,
    tags: ["calming", "lavender"],
  };

  const margin = (isOn("price") && isOn("retail_price") && p.retail_price && p.price)
    ? Math.round((1 - p.price / p.retail_price) * 100) : null;
  const badgeMeta = isOn("badge") && p.badge ? BADGE_STYLE[p.badge] : null;

  return (
    <div className="rounded-xl border border-border bg-white overflow-hidden" data-testid="buyer-preview-card">
      {label && (
        <div className="text-[10px] mono uppercase tracking-[0.22em] text-foreground/55 px-3 pt-3">
          {label}
        </div>
      )}
      <div className="p-3">
        {/* Image */}
        <div className="relative h-32 bg-secondary rounded-lg overflow-hidden mb-3 flex items-center justify-center p-2">
          {isOn("image") && p.image ? (
            <img src={p.image} alt={p.name} className="max-w-full max-h-full object-contain"/>
          ) : (
            <div className="w-full h-full grid place-items-center text-foreground/25">
              <Package className="w-10 h-10"/>
            </div>
          )}
          {/* Badges (top-left) */}
          <div className="absolute top-2 left-2 flex flex-col gap-1">
            {badgeMeta && (
              <span className={`px-2 py-0.5 rounded-full text-[9px] uppercase tracking-wider font-medium ${badgeMeta.cls}`}>
                <Star className="inline w-2.5 h-2.5 mr-0.5"/>{badgeMeta.l}
              </span>
            )}
          </div>
          {/* Fulfillment chip (top-right) */}
          {isOn("fulfillment") && p.fulfillment && (
            <div className="absolute top-2 right-2">
              <span className="px-2 py-0.5 rounded-full text-[9px] uppercase tracking-wider bg-white/95 border border-border text-foreground/75">
                {FULFILL_LABEL[p.fulfillment] || p.fulfillment}
              </span>
            </div>
          )}
        </div>

        {/* Gallery thumbnail strip — additional images beyond the primary */}
        {isOn("image") && Array.isArray(p.gallery_images) && p.gallery_images.length > 0 && (
          <div className="flex gap-1.5 mb-3 overflow-x-auto" data-testid="buyer-preview-gallery">
            {p.gallery_images.slice(0, 5).map((src, i) => (
              <div key={i} className="shrink-0 w-12 h-12 rounded-md bg-secondary border border-border overflow-hidden">
                <img src={src} alt="" className="w-full h-full object-cover" loading="lazy"/>
              </div>
            ))}
          </div>
        )}

        {/* Category + tags */}
        {(isOn("category") && p.category) || (isOn("tags") && p.tags && p.tags.length) ? (
          <div className="flex items-center gap-2 flex-wrap text-[9.5px] uppercase tracking-wider text-foreground/55 mb-1">
            {isOn("category") && p.category && <span>{p.category}</span>}
            {isOn("tags") && p.tags && p.tags.slice(0, 2).map((t, i) => (
              <span key={i} className="inline-flex items-center gap-0.5 text-foreground/40">
                <Tag className="w-2 h-2"/>{t}
              </span>
            ))}
          </div>
        ) : null}

        {/* Name */}
        {isOn("name") && (
          <div className="font-medium leading-tight text-[13px] mt-0.5">{p.name}</div>
        )}

        {/* Brand */}
        {isOn("brand") && p.brand && (
          <div className="text-[11px] text-foreground/55 mt-0.5 inline-flex items-center gap-1">
            <Building2 className="w-3 h-3"/>{p.brand}
          </div>
        )}

        {/* SKU (mono) */}
        {isOn("sku") && (
          <div className="text-[10px] mono text-foreground/45 mt-0.5">SKU · {p.sku}</div>
        )}

        {/* Description */}
        {isOn("description") && p.description && (
          <div className="text-[11px] text-foreground/65 mt-2 line-clamp-2 leading-relaxed">{p.description}</div>
        )}

        {/* Price block */}
        {isOn("price") && (
          <div className="mt-3 flex items-end justify-between">
            <div>
              <div className="text-[20px] font-medium leading-none">${Number(p.price || 0).toFixed(2)}</div>
              <div className="text-[10px] text-foreground/55 mt-1 space-y-0.5">
                {isOn("retail_price") && p.retail_price && (
                  <div>
                    Retail <span className="line-through">${Number(p.retail_price).toFixed(2)}</span>
                    {margin && <span className="ml-1 text-emerald-700">· {margin}%</span>}
                  </div>
                )}
                {isOn("map_price") && p.map_price && (
                  <div>MAP ${Number(p.map_price).toFixed(2)}</div>
                )}
              </div>
            </div>
            <div className="text-right text-[10px] text-foreground/55">
              {isOn("moq") && p.moq && <div className="mono">MOQ {p.moq}</div>}
              {isOn("stock") && p.stock !== undefined && p.stock !== null && (
                <div className={`mono ${p.stock === 0 ? "text-rose-600" : p.stock < 50 ? "text-amber-600" : ""}`}>
                  {p.stock} in stock
                </div>
              )}
              {isOn("case_size") && p.case_size && (
                <div className="mono">Case · {p.case_size}</div>
              )}
            </div>
          </div>
        )}

        {/* UPC — barcode (last) */}
        {isOn("upc") && p.upc && (
          <div className="text-[9.5px] mono text-foreground/45 mt-2 border-t border-border pt-2">UPC {p.upc}</div>
        )}

        {/* Custom data points — only fields marked visible_to_buyers */}
        {(() => {
          const cfs = p.custom_fields || {};
          const defs = (customFields || []).filter(f => f.visible_to_buyers && cfs[f.key] !== undefined && cfs[f.key] !== "");
          if (defs.length === 0) return null;
          return (
            <div className="mt-3 pt-3 border-t border-border">
              <div className="text-[9.5px] mono uppercase tracking-[0.22em] text-foreground/45 mb-1.5">Specifications</div>
              <div className="space-y-1">
                {defs.map(f => (
                  <div key={f.key} className="flex items-baseline justify-between gap-3 text-[11px]">
                    <span className="text-foreground/55 truncate">{f.label}</span>
                    <span className="text-foreground font-medium truncate">{renderCfValue(f, cfs[f.key])}</span>
                  </div>
                ))}
              </div>
            </div>
          );
        })()}

        {/* Add-to-order CTA — always visible (it's the seller's call to action, not a data field) */}
        <button disabled className="mt-3 w-full h-9 rounded-full text-[11px] font-medium inline-flex items-center justify-center gap-1.5 bg-foreground/10 text-foreground/55 cursor-not-allowed">
          <Plus className="w-3 h-3"/>Add to order
        </button>
      </div>
    </div>
  );
}
